import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor - add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Try to refresh token
                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                    const response = await axios.post(
                        `${API_URL}/auth/refresh`,
                        {},
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`
                            }
                        }
                    );

                    const { token } = response.data.data;
                    localStorage.setItem('token', token);

                    // Retry original request
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }
            } catch (refreshError) {
                // Refresh failed - logout user
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// =====================================================
// AUTH API
// =====================================================

export const authAPI = {
    login: async (credentials) => {
        const response = await api.post('/auth/login', credentials);
        return response.data;
    },

    logout: async () => {
        const response = await api.post('/auth/logout');
        return response.data;
    },

    getCurrentUser: async () => {
        const response = await api.get('/auth/me');
        return response.data;
    },

    changePassword: async (passwords) => {
        const response = await api.post('/auth/change-password', passwords);
        return response.data;
    }
};

// =====================================================
// USERS API
// =====================================================

export const usersAPI = {
    listUsers: async (params = {}) => {
        const response = await api.get('/users', { params });
        return response.data;
    },

    getUserById: async (userId) => {
        const response = await api.get(`/users/${userId}`);
        return response.data;
    },

    createUser: async (userData) => {
        const response = await api.post('/users', userData);
        return response.data;
    },

    updateUser: async (userId, updates) => {
        const response = await api.patch(`/users/${userId}`, updates);
        return response.data;
    },

    activateUser: async (userId) => {
        const response = await api.post(`/users/${userId}/activate`);
        return response.data;
    },

    deactivateUser: async (userId) => {
        const response = await api.post(`/users/${userId}/deactivate`);
        return response.data;
    },

    deleteUser: async (userId) => {
        const response = await api.delete(`/users/${userId}`);
        return response.data;
    },

    getUserAuditHistory: async (userId, params = {}) => {
        const response = await api.get(`/users/${userId}/audit`, { params });
        return response.data;
    },

    searchUsers: async (query) => {
        const response = await api.get('/users/search', { params: { query } });
        return response.data;
    },

    searchUserByDocument: async (documentNumber) => {
        const response = await api.get(`/users/document/${documentNumber}`);
        return response.data;
    },

    changeUserEmail: async (userId, newEmail) => {
        const response = await api.patch(`/users/${userId}/email`, { newEmail });
        return response.data;
    }
};

// =====================================================
// AUDIT API
// =====================================================

export const auditAPI = {
    getRecentActivity: async (params = {}) => {
        // If params is a number, convert it to an object with limit
        const queryParams = typeof params === 'number' ? { limit: params } : params;
        const response = await api.get('/audit/recent', { params: queryParams });
        return response.data;
    },

    getStatistics: async (startDate, endDate) => {
        const response = await api.get('/audit/stats', {
            params: { startDate, endDate }
        });
        return response.data;
    },

    getAuditStats: async () => {
        const response = await api.get('/audit/stats');
        return response.data;
    }
};

export default api;
